import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import { SETTINGS, Settings } from '../../../../app.settings';

import { AuditInfoComponent, CanComponentDeactivate } from '../../../../components';

import { FormsModule } from '@angular/forms';
import { firstValueFrom, forkJoin } from 'rxjs';
import { successMsg } from '../../../../common/messages';
import { notifyAndTrackError } from '../../../../common/error';
import { AppInsightsService } from '../../../../services/app-insights.service';
import {
  CatalogAdminService,
  DictionaryCacheService,
  ICatalogProject,
  ICatalogProjectUpdateRequest,
  IDictionaryEntry
} from '../../../../services/insecta';

@Component({
  selector: 'tab-projects',
  standalone: true,
  imports: [CommonModule, CardModule, CommonModule, TableModule, ToggleSwitchModule, AuditInfoComponent, FormsModule],

  templateUrl: 'tab-projects.component.html'
})
export class TabProjectsComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  dirtyConfirm$: ((leave: boolean) => void) | null = null;
  pageVars: any = {
    breadcrumbItems: <MenuItem[]>[{ label: 'Global Lists', routerLink: '/admin/globallists' }, { label: 'Site Catalog & Settings' }],
    isDirty: false,
    isEditing: false,
    isReady: false,
    pageId: `Admin:SiteCatalog`,
    showDirtyDlg: false
  };

  @Input() currentUser: string = ``;
  @Input() catalogProjects: ICatalogProject[] = [];
  @Input() catalogId: string = '';
  projects: IDictionaryEntry[] = [];
  @Output('isReady') isReady = new EventEmitter<boolean>();

  constructor(
    private appInsights: AppInsightsService,
    @Inject(SETTINGS) private settings: Settings,
    private dictionaryCache: DictionaryCacheService,
    private msgService: MessageService,
    private projectService: CatalogAdminService
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:SiteCatalog:ProjectsTab`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/sitecatalog#tabprojects`);
  }

  canDeactivate(): Promise<boolean> {
    if (!this.pageVars.isEditing) return Promise.resolve(true);

    return new Promise((resolve) => {
      this.pageVars.showDirtyDlg = true;
      const confirm$ = (decision: boolean) => {
        this.dirtyConfirm$ = null;
        resolve(decision);
      };
      this.dirtyConfirm$ = confirm$;
    });
  }

  dirtyConfirm(leave: boolean) {
    this.pageVars.showDirtyDlg = false;
    if (this.dirtyConfirm$) {
      this.dirtyConfirm$(leave);
    }
  }

  ngOnInit(): void {
    this.isReady.emit(false);
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Init`);
    this.pageVars.isReady = false;

    const request = {
      projects: this.dictionaryCache.get('project')
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.projects = response.projects;
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabProjectsComponent', action: 'Init:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
        this.isReady.emit(true);
      });
  }

  updateCatalogProject(catalogProject: ICatalogProject) {
    const request = <ICatalogProjectUpdateRequest>{
      id: catalogProject.id,
      isAvailable: catalogProject.isAvailable,
      isActive: catalogProject.isActive,
      actionBy: this.currentUser
    };

    firstValueFrom(this.projectService.updateCatalogProject(catalogProject.catalogId, request))
      .then(() => {
        const idx = this.catalogProjects.findIndex((x) => x.id === catalogProject.id);
        this.catalogProjects[idx].action = 'UPD';
        this.catalogProjects[idx].actionBy = this.currentUser;
        this.catalogProjects[idx].actionOn = new Date();

        successMsg(this.msgService, 'Catalog Project has been updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabProjectsComponent', action: 'UpdateCatalogProject:Error' });
      });
  }

  ngOnDestroy() {
    this.pageVars = {
      isReady: false
    };
    this.projects = [];
  }
}
